import {
  EContractStatus,
  ECreditApplicationStatus,
  ECreditApplicationStep,
  EDecision,
  EFinancingProgram,
  EOriginSystemId,
  EWorksheetStatus,
} from '@if/core/types'
import {
  anyDocsAwaitingApproval,
  anyDocsAwaitingUploadOrRefused,
  availableCreditEqualApprovedAmount,
  checkCanCreateContract,
  cvtAwaitingApproval,
} from '@src/data/creditapp-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { ApplicantDto, CreditAlertsDto, CreditApplication } from '@src/types/CreditApplicationSchema'
import { FilteredWorksheet } from '@src/types/FilteredWorksheet'
import { EIconType } from '../App/components/iconSection'
import { canPollCreditApp } from './credit-hooks'

function requiresMerchantIntervention(creditAlerts: CreditAlertsDto) {
  return (
    creditAlerts.hasDoBMismatch ||
    creditAlerts.hasNameMismatch ||
    creditAlerts.hasCreditLocked ||
    creditAlerts.hasInvalidCity ||
    creditAlerts.hasGeneralCreditReportError ||
    creditAlerts.isBanned
  )
}

export enum ECreditApplicationStatusMessage {
  Draft = 'draft',
  Active = 'active',
  Completed = 'completed',
  Cancelled = 'cancelled',
  Expired = 'expired',
  Declined = 'declined',
  ManualRevision = 'manualRevision',
  DocumentsRequired = 'documentsRequired',
  Error = 'applicationError',
  Progress = 'inProgress',
  Waiting = 'waiting',
  Processing = 'processing',
  ActionRequired = 'actionRequired',
  AwaitingSignature = 'awaitingSignature',
  AwaitingApproval = 'awaitingApproval',
  CreditReportNotFound = 'creditReportNotFound',
  SinRequired = 'sinRequired',
}

export const GetDefaultIconAndMessageFromDecision = (
  decision: EDecision | undefined,
  isprequal: boolean,
  creditApp: CreditApplication,
  isAnalysing: boolean,
  docsAwaitingApproval: boolean,
): { iconMessage: ECreditApplicationStatusMessage | null; iconType: EIconType | null } => {
  let iconMessage
  let iconType: EIconType | null = null

  const docsAwaitingUploadOrRefused = anyDocsAwaitingUploadOrRefused(
    creditApp.requiredDocuments,
    ECreditApplicationStep.Credit,
  )

  const requiresInterventionConfig =
    FinancingProgramConfigs[creditApp.financingProgramId as EFinancingProgram].requiresCreditStepIntervention

  const applicantRequiresIntervention =
    creditApp.applicant.creditAlerts != null ? requiresMerchantIntervention(creditApp.applicant.creditAlerts) : false
  const coApplicantRequiresIntervention =
    creditApp.coApplicant && creditApp.coApplicant.creditAlerts != null
      ? requiresMerchantIntervention(creditApp.coApplicant.creditAlerts)
      : false

  const requiresMerchantInterventionFlag =
    requiresInterventionConfig && (applicantRequiresIntervention || coApplicantRequiresIntervention)

  if (docsAwaitingUploadOrRefused && decision === EDecision.ManualRevision) {
    iconMessage = ECreditApplicationStatusMessage.DocumentsRequired
    iconType = EIconType.warning
    return { iconMessage, iconType }
  }

  if (docsAwaitingApproval && decision !== EDecision.Approved && decision !== EDecision.Refused) {
    iconMessage = ECreditApplicationStatusMessage.AwaitingApproval
    iconType = EIconType.info
    return { iconMessage, iconType }
  }

  if (creditApp.status === ECreditApplicationStatus.Cancelled) {
    iconMessage = ECreditApplicationStatusMessage.Cancelled
    iconType = EIconType.error
    return { iconMessage, iconType }
  }
  if ((!decision && !isAnalysing) || (creditApp.consentHardHit && !isAnalysing && decision === EDecision.Pending)) {
    iconMessage = ECreditApplicationStatusMessage.Error
    iconType = EIconType.warning
    return { iconMessage, iconType }
  }
  if (
    !creditApp.consentHardHit &&
    creditApp.status === ECreditApplicationStatus.Active &&
    (decision === EDecision.Error || decision === EDecision.ManualRevision) &&
    creditApp.originSystemId !== EOriginSystemId.MerchantDashboard
  ) {
    iconMessage = ECreditApplicationStatusMessage.ManualRevision
    iconType = EIconType.info
    return { iconMessage, iconType }
  }

  if (requiresMerchantInterventionFlag) {
    iconMessage = ECreditApplicationStatusMessage.ActionRequired
    iconType = EIconType.warning
    return { iconMessage, iconType }
  }

  if (
    creditApp.consentHardHit &&
    FinancingProgramConfigs[creditApp.financingProgramId as EFinancingProgram].worksheetIsNeededForCredit &&
    creditApp.worksheet === null &&
    isprequal === false &&
    creditApp.originSystemId === EOriginSystemId.Subscription
  ) {
    if (decision === EDecision.Refused) {
      iconMessage = ECreditApplicationStatusMessage.Declined
      iconType = EIconType.error
    } else {
      iconMessage = ECreditApplicationStatusMessage.AwaitingApproval
      iconType = EIconType.info
    }
    return { iconMessage, iconType }
  }

  switch (decision) {
    case EDecision.ManualRevision:
      iconMessage = ECreditApplicationStatusMessage.ManualRevision
      iconType = EIconType.waiting
      break
    case EDecision.Approved:
      iconMessage = ECreditApplicationStatusMessage.Completed
      iconType = EIconType.success
      break
    case EDecision.Refused:
      iconMessage = ECreditApplicationStatusMessage.Declined
      iconType = EIconType.error
      break
    case EDecision.Error:
      iconType = EIconType.warning
      iconMessage = ECreditApplicationStatusMessage.Error
      break
    default:
      iconType = null
      iconMessage = null
      break
  }
  return { iconMessage, iconType }
}

export const GetIconAndMessageForIncomeVerification = (
  applicant: ApplicantDto,
  canStartStep: boolean,
  hasIncomeDocumentsPendingReview: boolean,
  hasIncomeDocumentsPendingUpload: boolean,
  hasIncomeDocumentsApproved: boolean,
  isDone: boolean,
  applicationStatus: ECreditApplicationStatus,
  hasIdentityDocumentPendingReview: boolean,
  hasIdentityDocumentApproved: boolean,
) => {
  let iconMessage: ECreditApplicationStatusMessage | null = null
  let iconType: EIconType | null = null

  if (!canStartStep) {
    return { iconMessage, iconType }
  }

  if (applicationStatus === ECreditApplicationStatus.Cancelled) {
    iconMessage = ECreditApplicationStatusMessage.Cancelled
    iconType = EIconType.error
    return { iconMessage, iconType }
  }

  const mismatch = applicant.incomeVerification?.incomeMismatch || applicant.incomeVerification?.nameMismatch
  const isWaitingForClient = !!applicant.incomeVerification?.requiredExternalStep && !isDone && !mismatch
  const nameMismatchUnderReview =
    applicant.incomeVerification?.nameMismatch && (hasIdentityDocumentPendingReview || hasIdentityDocumentApproved)
  const hasDocumentsPendingUpload = hasIncomeDocumentsPendingUpload && !isDone && !nameMismatchUnderReview
  const isUnderReview =
    nameMismatchUnderReview ||
    (!hasIncomeDocumentsPendingUpload && hasIncomeDocumentsPendingReview) ||
    (hasIncomeDocumentsApproved && !isDone)
  const hasNotStartedThirdPartyVerification = !applicant.incomeVerification?.requiredExternalStep

  switch (true) {
    case isWaitingForClient:
      iconMessage = ECreditApplicationStatusMessage.Progress
      iconType = EIconType.info
      break
    case hasDocumentsPendingUpload:
      iconMessage = ECreditApplicationStatusMessage.DocumentsRequired
      iconType = EIconType.warning
      break
    case isDone:
      iconMessage = ECreditApplicationStatusMessage.Completed
      iconType = EIconType.success
      break
    case isUnderReview:
      iconMessage = ECreditApplicationStatusMessage.ManualRevision
      iconType = EIconType.waiting
      break
    case hasNotStartedThirdPartyVerification:
      iconMessage = ECreditApplicationStatusMessage.Waiting
      iconType = EIconType.warning
      break
    default:
  }

  return { iconMessage, iconType }
}

export const GetFundingIconAndMessage = (creditApp: CreditApplication) => {
  let iconMessage: ECreditApplicationStatusMessage
  let iconType: EIconType | null = null

  if (anyDocsAwaitingUploadOrRefused(creditApp.requiredDocuments, ECreditApplicationStep.PayOut)) {
    iconMessage = ECreditApplicationStatusMessage.DocumentsRequired
    iconType = EIconType.warning
    return { iconMessage, iconType }
  }
  if (anyDocsAwaitingApproval(creditApp.requiredDocuments)) {
    iconMessage = ECreditApplicationStatusMessage.ManualRevision
    iconType = EIconType.waiting
    return { iconMessage, iconType }
  }
  if (!creditApp.loanCreated) {
    iconMessage = ECreditApplicationStatusMessage.Processing
    iconType = EIconType.waiting
    return { iconMessage, iconType }
  }
  if (creditApp.loanCreated && creditApp.status !== ECreditApplicationStatus.Completed) {
    iconMessage = ECreditApplicationStatusMessage.Waiting
    iconType = EIconType.waiting
    return { iconMessage, iconType }
  }

  iconMessage = ECreditApplicationStatusMessage.Completed
  iconType = EIconType.success
  return { iconMessage, iconType }
}

export const GetContractIconAndMessage = (creditApp: CreditApplication, worksheet: FilteredWorksheet | null) => {
  let iconMessage: ECreditApplicationStatusMessage | null = null
  let iconType: EIconType | null = null
  const isCvtAwaitingApproval = cvtAwaitingApproval(creditApp.requiredDocuments)
  const isAvailableCreditEqualApprovaleAmount = availableCreditEqualApprovedAmount(creditApp)

  const canCreateContract = checkCanCreateContract(creditApp)

  if (canCreateContract) {
    iconMessage = ECreditApplicationStatusMessage.ActionRequired
    iconType = EIconType.warning
    return { iconMessage, iconType }
  }

  if (creditApp.status === ECreditApplicationStatus.Cancelled) {
    iconMessage = ECreditApplicationStatusMessage.Cancelled
    iconType = EIconType.error
    return { iconMessage, iconType }
  }
  if (anyDocsAwaitingUploadOrRefused(creditApp.requiredDocuments, ECreditApplicationStep.CVT)) {
    iconMessage = ECreditApplicationStatusMessage.DocumentsRequired
    iconType = EIconType.warning
    return { iconMessage, iconType }
  }

  if (
    anyDocsAwaitingApproval(
      creditApp.requiredDocuments.filter(
        (doc: { requiredBeforeStep: ECreditApplicationStep }) => doc.requiredBeforeStep === ECreditApplicationStep.CVT,
      ),
    )
  ) {
    iconMessage = ECreditApplicationStatusMessage.AwaitingApproval
    iconType = EIconType.waiting
    return { iconMessage, iconType }
  }

  if (
    (worksheet == null && !creditApp.hasTasksBeforeCvt) ||
    (worksheet?.status === EWorksheetStatus.Draft &&
      worksheet?.finalDecision.decision === EDecision.Pending &&
      !creditApp.hasTasksBeforeCvt) ||
    (worksheet?.status === EWorksheetStatus.Draft && isAvailableCreditEqualApprovaleAmount)
  ) {
    if (creditApp.originSystemId !== EOriginSystemId.MerchantDashboard) {
      iconMessage = ECreditApplicationStatusMessage.ManualRevision
      iconType = EIconType.waiting
    } else {
      iconMessage = ECreditApplicationStatusMessage.ActionRequired
      iconType = EIconType.warning
    }
    return { iconMessage, iconType }
  }
  if (
    worksheet?.finalDecision.decision === EDecision.ManualRevision ||
    isCvtAwaitingApproval ||
    creditApp.hasTasksBeforeCvt
  ) {
    iconMessage = ECreditApplicationStatusMessage.AwaitingApproval
    iconType = EIconType.waiting
    return { iconMessage, iconType }
  }
  if (
    worksheet?.finalDecision.decision === EDecision.Approved &&
    (creditApp.contract?.status === EContractStatus.Ready || creditApp.contract?.status === EContractStatus.Sending)
  ) {
    iconMessage = ECreditApplicationStatusMessage.AwaitingSignature
    iconType = EIconType.warning
    return { iconMessage, iconType }
  }
  if (worksheet?.status === EWorksheetStatus.Active && creditApp.contract?.status === EContractStatus.Completed) {
    iconMessage = ECreditApplicationStatusMessage.Completed
    iconType = EIconType.success
    return { iconMessage, iconType }
  }
  if (creditApp.contract.status === EContractStatus.NotReady && !canPollCreditApp(creditApp)) {
    iconMessage = ECreditApplicationStatusMessage.Error
    iconType = EIconType.warning
    return { iconMessage, iconType }
  }
  return { iconMessage, iconType }
}
